import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';

// types
import { InventoryItem, CALENDAR_VIEW } from 'types';
// assets
import { IoArrowBack } from 'react-icons/io5';
// components
import { LoadingIndicator } from 'components/LoadingIndicator';
// queries
import { useCreateUserCalendarEventMutation } from 'queries/createEvent/createUserCalendarEventMutation';
import { useTranslation } from 'react-i18next';

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  align-items: flex-start;
  @media screen and (max-width: 520px) {
    margin-top: -20px;
    display: block;
  }
`;

const Col = styled.div`
  box-sizing: border-box;
  background: ${props => props.theme.colors.white[100]};
  border: 1px solid #eeeff2;
  border-radius: 7px;
  padding: 24px;
  width: 384px;
  max-width: 100%;
  @media screen and (max-width: 520px) {
    border: 0;
    border-radius: 0;
    width: 100%;
    padding: 24px 0;
  }
`;

const GoBack = styled.div`
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${props => props.theme.colors.neutral[100]};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  margin-bottom: 32px;
  > span {
    margin-left: 8px;
  }
  @media screen and (max-width: 520px) {
    margin-top: 24px;
    margin-left: 20px;
  }
`;

const CalendlyWrapper = styled.div`
  height: calc(100vh - 230px);
  max-height: 740px;
  width: 100%;
`;

type CalendlyPrefillData = {
  email: string;
  name: string;
  customAnswers: any;
};

type CalendlyData = {
  url: string;
  prefill: CalendlyPrefillData;
};

type Props = {
  orderDetails: any;
  onChangeView: (view: CALENDAR_VIEW) => void;
  inventoryItem?: InventoryItem;
};

export const SchedulingDriveCalendly = ({
  orderDetails,
  onChangeView,
  inventoryItem,
}: Props) => {
  const themes = useTheme();
  const { t } = useTranslation();
  const [calendlyData, setCalendlyData] = useState<CalendlyData | undefined>();
  const {
    mutateAsync: createUserCalendarEventMutation,
    isLoading: isLoadingUserEvent,
  } = useCreateUserCalendarEventMutation();
  useEffect(() => {
    if (!orderDetails?.calendar?.calendlyCalendarUrl) return;
    const calendlyUrl = orderDetails.calendar.calendlyCalendarUrl;
    const url =
      calendlyUrl +
      (calendlyUrl.includes('?') ? '&' : '?') +
      'hide_event_type_details=1&hide_gdpr_banner=1';
    setCalendlyData({
      url: url,
      prefill: {
        name: `${orderDetails.firstName || ''} ${orderDetails.lastName || ''}`,
        email: orderDetails.email || '',
        customAnswers: {
          a1: `${inventoryItem?.make} ${inventoryItem?.model} ${inventoryItem?.stockNumber}`,
        },
      },
    });
  }, [orderDetails]);

  const handleCalendly = async (payload: any) => {
    await createUserCalendarEventMutation({ data: payload });
    window.location.reload();
  };

  useCalendlyEventListener({
    onEventScheduled: e => handleCalendly(e.data.payload),
  });

  return (
    <>
      {isLoadingUserEvent && (
        <LoadingIndicator
          isLoading={isLoadingUserEvent}
          height={'30vh'}
          text={t('Loading...')}
          color={themes.colors.white[100]}
        />
      )}
      {!isLoadingUserEvent && (
        <Row>
          <Col>
            <GoBack onClick={() => onChangeView(CALENDAR_VIEW.LANDING)}>
              <IoArrowBack size={18} />
              <span>{t('Back to Offer')}</span>
            </GoBack>
            {calendlyData && (
              <CalendlyWrapper>
                <InlineWidget {...calendlyData} />
              </CalendlyWrapper>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};
